/**
 * **版面装置** —— 把界面当版面排的那一层。（原来叫 `screens/chrome.ts`，
 * 搬到组件库里来了：它是**可复用的装置**，不是某一屏的私有代码。）
 *
 * 直接落地 `docs/ui-design.md` §5 与 `visual-design.md` §4.1「逻辑画布 = 一张纸」：
 *
 * | 元素 | 参数 |
 * | --- | --- |
 * | 分栏竖线 | 4 栏，x = 240/560/880/1200，alpha 0.06 |
 * | 页眉行 | y = 34，一条线 + 左对齐机读字 |
 * | 页码 | 右下角 `P.01`，alpha 0.3 |
 * | 注册标记 | 四角 ⌐ 形，alpha 0.15 |
 *
 * ⚠️ 这一层**必须极淡**（alpha ≤ 0.1 量级）。它的作用是让大脑把画面归类成
 * "印刷品"，过了就变成"背景有脏东西"。所以下面所有 alpha 都是写死的常数，
 * 不是可调项 —— 它们不该被顺手调大。
 */

import { Container, Graphics } from 'pixi.js';
import { BASE, LIGHT, PRESS } from '../palette';
import { GAP, INK, TRACKING, uiLine, uiPad } from './style';
import { richLabel } from './richText';
import type { Placeable, Widget } from './base';

/** 分栏竖线的位置（逻辑画布上的 x）。设计规则给死了四个值 */
export const COLUMN_RULES = [240, 560, 880, 1200] as const;

const FAINT = 0.06;
const MARK_ALPHA = 0.15;
const PAGE_ALPHA = 0.3;

export interface ChromeSpec {
  readonly scale: number;
  readonly width: number;
  readonly height: number;
  /** 页眉左侧的机读字。惯例是全大写英文 + 中文 */
  readonly header: string;
  /** 页码，例如 `P.01` */
  readonly page: string;
}

/**
 * 画一整套版面装置。返回的容器**不接收指针事件** ——
 * 它是装饰，不该挡住任何按钮。
 */
export function pageChrome(spec: ChromeSpec): Container {
  const { scale, width, height } = spec;
  const container = new Container();
  container.label = 'page-chrome';
  container.eventMode = 'none';

  const rules = new Graphics();
  for (const x of COLUMN_RULES) {
    if (x >= width) continue;
    rules.rect(x, 0, 1, height).fill({ color: INK.text, alpha: FAINT });
  }
  container.addChild(rules);

  // 页眉：一条横线 + 左对齐的机读字
  const headerY = uiPad(34, scale);
  const headerRule = new Graphics()
    .rect(0, headerY, width, 1)
    .fill({ color: INK.text, alpha: MARK_ALPHA });
  const headerText = richLabel({
    text: spec.header.toUpperCase(),
    scale,
    size: 10,
    fill: INK.textDim,
    role: 'mono',
    tracking: TRACKING.wide,
  });
  headerText.position.set(uiPad(24, scale), headerY - headerText.height - 4);
  headerText.alpha = 0.75;
  container.addChild(headerRule, headerText);

  // 页码
  const page = richLabel({
    text: spec.page,
    scale,
    size: 10,
    fill: INK.textDim,
    role: 'mono',
    tracking: TRACKING.wide,
  });
  page.anchor.set(1, 1);
  page.position.set(width - uiPad(24, scale), height - uiPad(16, scale));
  page.alpha = PAGE_ALPHA;
  container.addChild(page);

  // 四角注册标记（⌐ 形的四条边）
  const inset = uiPad(14, scale);
  const arm = uiPad(14, scale);
  const marks = new Graphics();
  const corner = (x: number, y: number, dx: number, dy: number): void => {
    marks.rect(x, y, arm * dx, 1).fill({ color: INK.text, alpha: MARK_ALPHA });
    marks.rect(x, y, 1, arm * dy).fill({ color: INK.text, alpha: MARK_ALPHA });
  };
  corner(inset, inset, 1, 1);
  corner(width - inset, inset, -1, 1);
  corner(inset, height - inset, 1, -1);
  corner(width - inset, height - inset, -1, -1);
  container.addChild(marks);

  return container;
}

// ============================================================
// 文字装置
// ============================================================

export type NoticeTone = 'info' | 'warn' | 'danger';

export interface NoticeSpec {
  readonly text: string;
  readonly scale: number;
  readonly width: number;
  readonly tone?: NoticeTone;
}

/**
 * 一条通栏提示。
 *
 * 用它而不是"一行红字"：印刷品里没有孤零零的红字，
 * 强调是靠**一块印出来的东西**（左边一条粗色条 + 通栏底纹）。
 */
export function notice(spec: NoticeSpec): Widget {
  const { scale, width } = spec;
  const tone = spec.tone ?? 'info';
  const color = tone === 'danger' ? INK.danger : tone === 'warn' ? INK.warning : INK.textDim;

  const bar = uiPad(3, scale);
  const pad = uiPad(10, scale);
  const text = richLabel({
    text: spec.text,
    scale,
    size: 12,
    fill: tone === 'info' ? INK.textDim : color,
    role: 'cjk',
    // 让开左边那条色条
    wrapWidth: width - bar - pad * 2,
  });

  const height = text.height + pad * 2;
  const container = new Container();
  const background = new Graphics().rect(0, 0, width, height).fill({ color: INK.panel, alpha: 0.8 });
  const edge = new Graphics().rect(0, 0, bar, height).fill({ color, alpha: 0.9 });
  text.position.set(bar + pad, pad);
  container.addChild(background, edge, text);

  return { container, width, height };
}

/**
 * 一条**通栏大标题** —— 设计规则里的"通栏大标题，其他内容让位"。
 *
 * 用反白块而不是大号彩色字：构成主义靠**黑白颠倒**制造层级，
 * 靠颜色只会把它拉回"深色 UI"。
 */
export function banner(spec: {
  readonly text: string;
  readonly scale: number;
  readonly width: number;
  readonly sub?: string;
}): Widget {
  const { scale, width } = spec;
  const pad = uiPad(14, scale);
  const height = uiPad(spec.sub === undefined ? 52 : 76, scale);

  const container = new Container();
  const background = new Graphics().rect(0, 0, width, height).fill({ color: INK.knockoutFill });
  const text = richLabel({
    text: spec.text,
    scale,
    size: 26,
    fill: INK.onKnockout,
    role: 'display',
    tracking: TRACKING.normal,
  });
  text.position.set(pad, pad - uiPad(2, scale));
  container.addChild(background, text);

  if (spec.sub !== undefined) {
    const sub = richLabel({
      text: spec.sub,
      scale,
      size: 13,
      fill: INK.onKnockout,
      role: 'cjk',
    });
    sub.alpha = 0.72;
    sub.position.set(pad, pad + text.height + 2);
    container.addChild(sub);
  }

  return { container, width, height };
}

/** 一条细的分区标题：`— 玩家 (2/5) ————————` */
export function sectionTitle(spec: {
  readonly text: string;
  readonly scale: number;
  readonly width: number;
}): Widget {
  const { scale, width } = spec;
  const text = richLabel({
    text: spec.text.toUpperCase(),
    scale,
    size: 11,
    fill: INK.accent,
    role: 'mono',
    bold: true,
    tracking: TRACKING.wide,
  });
  const container = new Container();
  const lineY = Math.round(text.height / 2);
  const gap = uiPad(10, scale);
  const line = new Graphics()
    .rect(text.width + gap, lineY, Math.max(0, width - text.width - gap), 1)
    .fill({ color: INK.edge, alpha: 0.7 });
  container.addChild(text, line);
  return { container, width, height: text.height };
}

/** 一行"标签 —— 值"的机读行，用来排只读信息 */
export function infoRow(spec: {
  readonly label: string;
  readonly value: string;
  readonly scale: number;
  readonly width: number;
  readonly valueFill?: number;
}): Widget {
  const { scale, width } = spec;
  const name = richLabel({ text: spec.label, scale, size: 12, fill: INK.textDim, role: 'cjk' });
  const value = richLabel({
    text: spec.value,
    scale,
    size: 12,
    fill: spec.valueFill ?? INK.text,
    role: 'mono',
    align: 'right',
    tracking: TRACKING.normal,
  });
  value.anchor.set(1, 0);
  const height = Math.max(name.height, value.height);
  value.position.set(width, Math.round((height - value.height) / 2));
  name.position.set(0, Math.round((height - name.height) / 2));
  const container = new Container();
  container.addChild(name, value);
  return { container, width, height };
}

// ============================================================
// 印章 / 徽章
// ============================================================

export type StampTone = 'knockout' | 'accent' | 'ok' | 'warning' | 'danger' | 'dim';

const STAMP_TONE: Record<StampTone, { fill: number; ink: number }> = {
  /** 白底黑字。权限的默认章 */
  knockout: { fill: INK.knockoutFill, ink: INK.onKnockout },
  accent: { fill: INK.primary, ink: INK.onKnockout },
  ok: { fill: INK.ok, ink: INK.onKnockout },
  warning: { fill: INK.warning, ink: INK.onKnockout },
  danger: { fill: INK.danger, ink: INK.onKnockout },
  /** 没着墨的一档。用它表示"这个身份存在但不生效" */
  dim: { fill: BASE.sunken, ink: LIGHT.dim },
};

export interface StampSpec {
  readonly text: string;
  readonly scale: number;
  readonly tone?: StampTone;
  readonly size?: number;
}

/**
 * **一枚小印章**（徽章）。
 *
 * ⚠️ 和 `stampButton` 的区别：这个**不可交互**，而且**不出血**
 * （`docs/ui-design.md` §4.3）。打样页那条纪律是
 * 「标记：小、被框住、不出血」—— 徽章属于标记。
 */
export function stamp(spec: StampSpec): Widget {
  const scale = spec.scale;
  const padX = uiPad(6, scale);
  const padY = uiPad(3, scale);
  const tone = STAMP_TONE[spec.tone ?? 'knockout'];
  const text = richLabel({
    text: spec.text,
    scale,
    size: spec.size ?? 10,
    fill: tone.ink,
    role: 'cjk',
    bold: true,
  });
  const width = Math.ceil(text.width + padX * 2);
  const height = Math.ceil(text.height + padY * 2);
  const container = new Container();
  container.addChild(new Graphics().rect(0, 0, width, height).fill({ color: tone.fill }));
  text.position.set(padX, padY);
  container.addChild(text);
  return { container, width, height };
}

// ============================================================
// 信号条 —— 打样页那个 .uibar
// ============================================================

export type SignalKind = 'break' | 'hazard';

export interface SignalBarSpec {
  readonly kind: SignalKind;
  readonly scale: number;
  readonly width: number;
  /** 左端色块的宽度（设计像素）。默认 180，和打样页一致 */
  readonly headWidth?: number;
  readonly label: string;
  /** 右端的机读字 */
  readonly code?: string;
}

/**
 * **等待响应 / 致命窗口的通栏信号条。**
 *
 * 这是 `docs/proof/card-faces.html` 里那个 `.uibar` 的落地 ——
 * 打样页早就画出来了，界面里一直没实现（`docs/ui-design.md` §6）。
 *
 * | kind | 左端 | 用在哪 |
 * | --- | --- | --- |
 * | `break` | 酸青实心块（= 断点） | 有人还没表态，等窗口 |
 * | `hazard` | 45° 红白危险条纹 | 致命响应窗口 |
 *
 * ⚠️ **危险条纹只有两个出处**：`Exception` 白卡的底边 + 这里。
 * 用第二次就变成套路，不再是威胁（`visual-design.md` §6.1）。
 * 条纹不含语言，所以它会先变成"死亡"的条件反射，再被读懂。
 */
export function signalBar(spec: SignalBarSpec): Widget {
  const scale = spec.scale;
  const height = uiPad(32, scale);
  const head = uiPad(spec.headWidth ?? 180, scale);
  const edge = uiLine(1, scale);

  const container = new Container();
  const box = new Graphics()
    .rect(0, 0, spec.width, height)
    .stroke({ color: INK.rule, width: edge, alignment: 0 });
  container.addChild(box);

  // 左端：断点，或者危险条纹
  const headGraphics = new Graphics();
  if (spec.kind === 'hazard') {
    drawHazardStripes(headGraphics, 0, 0, head, height, scale);
  } else {
    headGraphics.rect(0, 0, head, height).fill({ color: INK.waiting });
  }
  container.addChild(headGraphics);

  // 标签条：黑底、白字、拉字距。左中文右机读
  const labelText = richLabel({
    text: spec.label,
    scale,
    size: 11,
    fill: INK.text,
    role: 'cjk',
    bold: true,
    tracking: TRACKING.wide,
  });
  labelText.position.set(head + uiPad(14, scale), Math.round((height - labelText.height) / 2));
  container.addChild(labelText);

  if (spec.code !== undefined) {
    const code = richLabel({
      text: spec.code,
      scale,
      size: 10,
      fill: spec.kind === 'hazard' ? INK.danger : INK.waiting,
      role: 'mono',
      bold: true,
      tracking: TRACKING.wide,
    });
    code.anchor.set(1, 0);
    code.position.set(spec.width - uiPad(14, scale), Math.round((height - code.height) / 2));
    container.addChild(code);
  }

  return { container, width: spec.width, height };
}

/**
 * 45° 危险条纹。
 *
 * ⚠️ 手画斜条而不是用渐变/贴图：这是**唯一**允许出现在 UI 里的"红白条纹"，
 * 它得和 `Exception` 白卡底边那条是同一种东西 —— 所以画法要一样，
 * 都是同一个几何：白底 + 斜 45° 的红带。
 */
export function drawHazardStripes(
  graphics: Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  scale: number,
): void {
  const stripe = uiPad(9, scale);
  graphics.rect(x, y, width, height).fill({ color: LIGHT.base });
  for (let offset = -height - stripe * 2; offset < width + height; offset += stripe * 2) {
    const bottomLeft = x + offset;
    graphics
      .poly([
        bottomLeft, y + height,
        bottomLeft + stripe, y + height,
        bottomLeft + stripe + height, y,
        bottomLeft + height, y,
      ])
      .fill({ color: PRESS.red });
  }
}

// ============================================================
// 竖排光标 + 析构登记
// ============================================================

export interface Column {
  readonly container: Container;
  readonly y: number;
  setY(value: number): void;
  /**
   * 竖着摆一个东西，返回它**自己（原类型）**。
   *
   * 泛型是刻意的：调用方常常要先造再配置（`const b = col.add(button(...)); b.setEnabled(false)`）。
   * 返回 `Placeable` 的话就丢掉了 `Button` 上的方法，逼得调用方写下
   * 先建后摆的两行 —— 那两行里最容易出的错就是**忘了摆**（按钮不见了）。
   */
  add<T extends Placeable>(item: T, x?: number, extraGap?: number): T;
  /** 摆一个自己管位置的东西（版面装置、横幅） */
  addRaw(child: Container): void;
  /** 登记一个析构函数 */
  track(dispose: () => void): void;
  /** 释放所有登记过的东西，并清空容器 */
  dispose(): void;
}

/**
 * 屏幕版面用的**竖排光标**，顺便记着哪些东西要手动释放。
 *
 * 两件事捆在一起是有原因的：
 *
 * 1. 菜单的版面全是"从上往下摆"，一个 `y` 游标就够，不需要引入布局引擎
 * 2. ⚠️ **滑块在舞台上挂了监听器，`container.destroy()` 不会摘掉它们。**
 *    所以必须有一个地方统一登记 `dispose`。之前试着靠"每个屏幕自己记得"，
 *    结果是改 UI 倍率重建一次就多一份幽灵滑块 —— 拖一下，所有历史实例一起响应。
 *
 * 规矩：**凡是 `track()` 没登记的滑块，都是 bug。**
 */
export function createColumn(scale: number, gap: number = GAP.normal): Column {
  const container = new Container();
  const disposers: (() => void)[] = [];
  let cursor = 0;

  return {
    container,
    get y() {
      return cursor;
    },
    setY(value: number) {
      cursor = value;
    },
    add(item, x = 0, extraGap = 0) {
      // `Widget` 是普通接口、不是 Container 的子类，所以这个 instanceof 能正确分流
      const node = item instanceof Container ? item : item.container;
      node.position.set(x, cursor);
      container.addChild(node);
      cursor += item.height + uiPad(gap, scale) + extraGap;
      return item;
    },
    addRaw(child) {
      container.addChild(child);
    },
    track(dispose) {
      disposers.push(dispose);
    },
    dispose() {
      for (const dispose of disposers) dispose();
      disposers.length = 0;
      container.removeChildren();
    },
  };
}
